import { toast } from "./use-toast"
import { completePendingLogout } from "../lib/logoutSession"
import { LOGOUT_STORAGE_KEY, hasExplicitLogout, getLogoutState } from "../utils/authStorage"
import { clearAuth } from "../redux/slices/authSlice"
import { useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../redux/hooks"
import { loginUser, logoutUser, checkAuthStatus, clearError } from "../redux/slices/authSlice"
import { useEffect } from "react"




export function useAuthRedux() {
  const dispatch = useAppDispatch()
  const { user, isAuthenticated, loading, error, passwordChangeSuccess } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()

  useEffect(() => {
    // Authentication state should not navigate on app startup. A cached
    // session is only an offline fallback; the route guard must decide
    // access after the session check completes. Navigation to dashboard is
    // reserved for a successful login.
    completePendingLogout()
    dispatch(checkAuthStatus())
  }, [dispatch])

  useEffect(() => {
    const handleUnauthorized = async () => {
      await dispatch(logoutUser())
      navigate("/", { replace: true })
    }

    window.addEventListener("auth:unauthorized", handleUnauthorized)
    return () => window.removeEventListener("auth:unauthorized", handleUnauthorized)
  }, [dispatch, navigate])

  useEffect(() => {
    const finishLogout = () => { completePendingLogout() }
    const handleStorage = (event) => {
      if (event.key === LOGOUT_STORAGE_KEY && hasExplicitLogout()) {
        dispatch(clearAuth())
        navigate("/", { replace: true })
      }
    }
    window.addEventListener("online", finishLogout)
    window.addEventListener("storage", handleStorage)
    return () => {
      window.removeEventListener("online", finishLogout)
      window.removeEventListener("storage", handleStorage)
    }
  }, [dispatch, navigate])

  const login = async (credentials) => {
    try {
      const result = await dispatch(loginUser(credentials)).unwrap()

      toast.success("Signed in successfully")

      // Redirect based on user role
      if (result.user.role === "admin") {
        // navigate("/admin")
        navigate("/dashboard")

      } else {
        navigate("/dashboard")
      }

      return result
    } catch (error) {
      toast.error("Unable to sign in", error?.message || (typeof error === "string" ? error : "Please try again."))
      throw error
    }
  }

  const logout = async () => {
    const request = dispatch(logoutUser())
    navigate("/", { replace: true })
    toast.success("Signed out successfully")
    await request
    if (navigator.onLine !== false && getLogoutState()?.pending) {
      toast.info("Signed out on this device", "Server sign-out will retry when the connection is available.")
    }
  }

  const clearAuthError = () => {
    dispatch(clearError())
  }

  return {
    user,
    isAuthenticated,
    isLoading: loading,
    error,
    login,
    logout,
    clearError: clearAuthError,
    passwordChangeSuccess,
  }
}
